import { useState } from "react";

import { AncillaryDocumentResponse } from "../../../common/api/catalogue-api/ancillary/AncillaryDocumentResponse";
import { SearchAncillariesRequest } from "../../../common/api/catalogue-api/ancillary/SearchAncillariesRequest";
import { SearchAncillariesResponse } from "../../../common/api/catalogue-api/ancillary/SearchAncillariesResponse";
import { useCatalogueAncillarySearchApi } from "../../../common/api/catalogue-api/ancillary/useCatalogueAncillarySearchApi";
import { useAssetsApi } from "../../../common/api/master-api/assets/useAssetsApi";
import { SearchAssetsContentAggregationsResponse } from "../../../common/api/master-api/search/content-requests-reposnses/SearchAssetsContentAggregationsResponse";
import { AncillaryContentSubtype } from "../../../common/types/AncillaryContentType";
import { IdAggregation } from "../../../common/types/IdAggregation";
import { AncillaryAssetsData, AncillarySearchItem } from "./AncillarySearchItem";

type AncillarySearchArgs = {
    query: string;
    ancillaryContentType: AncillaryContentSubtype;
    types: number[];
};
type AncillarySearchLastModifiedArgs = Omit<AncillarySearchArgs, "query">;

export const useAncillarySearchListPage = () => {
    const { searchAncillaries } = useCatalogueAncillarySearchApi();
    const { searchAssetsContentAggregations } = useAssetsApi();

    const [items, setItems] = useState<AncillarySearchItem[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [loadMoreParameters, setLoadMoreParameters] = useState<SearchAncillariesRequest>();
    const [cachedAggregations, setCachedAggregations] = useState<SearchAssetsContentAggregationsResponse>({
        aggregations: []
    });
    const [isMoreLoading, setIsMoreLoading] = useState<boolean>(false);

    const pageSize = 50;

    const search = async ({ query, ancillaryContentType, types }: AncillarySearchArgs) => {
        if (query) {
            searchByQuery({ query, ancillaryContentType, types });
        } else {
            searchLastModified({ ancillaryContentType, types });
        }
    };

    const searchLastModified = async ({ ancillaryContentType, types }: AncillarySearchLastModifiedArgs) => {
        setIsLoading(true);

        const subtype = ancillaryContentType;

        const aggregationsResponse = await searchAssetsContentAggregations({ subtype: subtype, types });
        setCachedAggregations(aggregationsResponse);

        if (!aggregationsResponse.aggregations.length) {
            setListEmpty();
            setIsLoading(false);
            return;
        }

        const ancillaryIds = aggregationsResponse.aggregations.slice(0, pageSize).map((agg) => agg.id);

        searchAncillaryByIds(ancillaryIds, subtype)
            .then((response: SearchAncillariesResponse) => {
                setTotalCount(response.totalCount);

                const ancillaryItems = mapAndSortResponseToLastModifiedAncillaryItems(
                    response,
                    aggregationsResponse.aggregations
                );

                setItems(ancillaryItems);

                setLoadMoreParameters({ query: "", page: 2, pageSize, subtype, ancillaryIds });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const searchByQuery = async ({ query, ancillaryContentType, types }: AncillarySearchArgs) => {
        setIsLoading(true);

        const subtype = ancillaryContentType;

        const aggregationsResponse = await searchAssetsContentAggregations({ query, subtype: subtype, types });
        setCachedAggregations(aggregationsResponse);

        if (!aggregationsResponse.aggregations.length) {
            setIsLoading(false);
            setListEmpty();
            return;
        }
        const ancillaryIds = aggregationsResponse.aggregations.map((agg) => agg.id);

        searchAncillaries({ query, page: 1, pageSize, subtype, ancillaryIds })
            .then((response: SearchAncillariesResponse) => {
                setTotalCount(response.totalCount);

                const ancillaryItems = mapResponseToAncillaryItems(response, aggregationsResponse.aggregations);

                setItems(ancillaryItems);

                setLoadMoreParameters({ query, page: 2, pageSize, subtype, ancillaryIds });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const loadMore = async () => {
        if (!loadMoreParameters) {
            throw new Error("Parameters for 'load more' must not be empty.");
        }

        if (loadMoreParameters.query) {
            loadMoreByQuery(loadMoreParameters);
        } else {
            loadMoreLastModified(loadMoreParameters);
        }
    };

    const loadMoreByQuery = async (searchParameters: SearchAncillariesRequest) => {
        setIsMoreLoading(true);

        searchAncillaries(searchParameters)
            .then((response: SearchAncillariesResponse) => {
                setTotalCount(response.totalCount);

                const ancillaryItems = mapResponseToAncillaryItems(response, cachedAggregations.aggregations);

                setItems([...items, ...ancillaryItems]);

                setLoadMoreParameters({
                    ...searchParameters,
                    page: searchParameters.page + 1
                });
            })
            .finally(() => {
                setIsMoreLoading(false);
            });
    };

    const loadMoreLastModified = async (searchParameters: SearchAncillariesRequest) => {
        setIsLoading(true);

        const ancillaryIds = cachedAggregations.aggregations
            .slice((searchParameters.page - 1) * pageSize, searchParameters.page * pageSize)
            .map((agg) => agg.id);

        searchAncillaryByIds(ancillaryIds, searchParameters.subtype)
            .then((response: SearchAncillariesResponse) => {
                setTotalCount(cachedAggregations.aggregations.length);

                const ancillaryItems = mapAndSortResponseToLastModifiedAncillaryItems(
                    response,
                    cachedAggregations.aggregations
                );

                setItems([...items, ...ancillaryItems]);

                setLoadMoreParameters({
                    ...searchParameters,
                    page: searchParameters.page + 1
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const searchAncillaryByIds = async (
        ancillaryIds: string[],
        subtype: AncillaryContentSubtype
    ): Promise<SearchAncillariesResponse> => {
        return searchAncillaries({ query: "", pageSize, page: 1, ancillaryIds, subtype });
    };

    const setListEmpty = () => {
        setItems([]);
        setTotalCount(0);
    };

    return {
        items,
        totalCount,
        isLoading,
        isMoreLoading,
        pageSize,
        search,
        loadMore
    };
};

const mapResponseToAncillaryItems = (response: SearchAncillariesResponse, aggregations: IdAggregation[]) => {
    return response.items.map((item: AncillaryDocumentResponse) => {
        const agg = aggregations.find((aggregation) => aggregation.id === item.id);

        if (!agg) throw new Error("Aggregateion should not be undefined");

        return mapToAncillarySearchItem(item, { assetsCount: agg.count, types: agg.types });
    });
};

const mapToAncillarySearchItem = (
    ancillary: AncillaryDocumentResponse,
    { assetsCount, types }: AncillaryAssetsData
): AncillarySearchItem => {
    return {
        id: ancillary.id,
        title: ancillary.title,
        distributor: ancillary.version.distributor?.name || "",
        assetsCount,
        types
    };
};

const mapAndSortResponseToLastModifiedAncillaryItems = (
    response: SearchAncillariesResponse,
    aggregations: IdAggregation[]
) => {
    const ancillarySearchItems: AncillarySearchItem[] = [];

    aggregations.forEach((agg) => {
        const searchItem = response.items.find((item) => item.id === agg.id);
        if (searchItem) {
            ancillarySearchItems.push(
                mapToAncillarySearchItem(searchItem, { assetsCount: agg.count, types: agg.types })
            );
        }
    });

    return ancillarySearchItems;
};
